import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import PinDropIcon from '@material-ui/icons/PinDrop';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import PlaceAddressLinkDialog from './PlaceAddressLinkDialog';
import AddAddressDialog from './AddAddressDialog';
import { getPlaceAddresses, deletePlaceAddressLink } from '../../services/userService';
import { ADDRESS_GRID_COLUMNS } from '../../constants/placeAddressesTabConstants';

const PlaceAddressesTab = ({ ptsPlaceID }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editAddressId, setEditAddressId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canEditPlace = getFullPermissions('globals', 'Places', 'any').Edit;
  const addressPerms = getFullPermissions('globals', 'Addresses', 'any');
  const canViewAddressRecord = Boolean(addressPerms.Read || addressPerms.Edit);

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadAddresses = useCallback(async () => {
    if (!ptsPlaceID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPlaceAddresses(ptsPlaceID),
      onSuccess: (data) => setAddresses(data),
    });
  }, [dispatch, ptsPlaceID]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const handleAdd = useCallback(() => {
    setEditingLink(null);
    setLinkFormOpen(true);
  }, []);

  const handleManagePrimary = useCallback((row) => {
    setEditingLink(row);
    setLinkFormOpen(true);
  }, []);

  const handleOpenAddressRecord = useCallback((row) => setEditAddressId(row.ptsAddressID), []);

  const handleLinkFormClose = useCallback(() => {
    setLinkFormOpen(false);
    setEditingLink(null);
  }, []);

  const handleLinkSaved = useCallback(() => {
    handleLinkFormClose();
    loadAddresses();
  }, [handleLinkFormClose, loadAddresses]);

  const handleAddressRecordSaved = useCallback(() => {
    setEditAddressId(null);
    loadAddresses();
  }, [loadAddresses]);

  const performDelete = useCallback(
    async (row) => {
      await executeApiAction({
        setLoading: setDeleting,
        dispatch,
        action: () => deletePlaceAddressLink({ ptsPlaceID, ptsAddressID: row.ptsAddressID }),
        onSuccess: () => {
          dispatch(notify('Address removed successfully', 'success'));
          loadAddresses();
        },
      });
    },
    [dispatch, loadAddresses, ptsPlaceID]
  );

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to remove this "${
          row.FullAddressText || 'address'
        }" from this place?`,
        () => performDelete(row),
        'Remove Address'
      );
    },
    [performDelete, requestConfirm]
  );

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Address"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPlace}
        />
      </Box>

      <CommonDataGrid
        rows={addresses}
        columns={ADDRESS_GRID_COLUMNS}
        getRowId={(row) => row.ptsAddressID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Place Addresses',
        }}
        actions={{
          custom: [
            {
              icon: <EditIcon fontSize="small" color="primary" />,
              tooltip: 'Manage Primary',
              onClick: handleManagePrimary,
              disabled: !canEditPlace,
            },
            canViewAddressRecord && {
              icon: <PinDropIcon fontSize="small" color="primary" />,
              tooltip: 'Open Address Record',
              onClick: handleOpenAddressRecord,
              disabled: !canEditPlace,
            },
          ].filter(Boolean),
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPlace },
        }}
      />

      {linkFormOpen && (
        <PlaceAddressLinkDialog
          open={linkFormOpen}
          ptsPlaceID={ptsPlaceID}
          addressLink={editingLink}
          onClose={handleLinkFormClose}
          onSaved={handleLinkSaved}
        />
      )}

      {editAddressId && (
        <AddAddressDialog
          open={Boolean(editAddressId)}
          addressId={editAddressId}
          onClose={() => setEditAddressId(null)}
          onAddressCreated={handleAddressRecordSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default PlaceAddressesTab;
